import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {ManagePlace} from '../shared/models/manage-place.model';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {City} from '../shared/models/city.model';
import {Neighborhood} from '../shared/models/neighborhood.model';
import {PublicsAreaSettingsModel} from '../shared/models/publics-area-settings.model';
import {HttpClient} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {FestivalService} from '../shared/services/festival.service';
import {ManageDataService} from '../shared/services/manage-data.service';
import {faTrash} from '@fortawesome/free-solid-svg-icons';
import {environment} from '../../environments/environment';
import * as moment from 'moment';
import {ManagePlaceService} from '../shared/services/manage-place-service.service';

@Component({
  selector: 'app-edit-place',
  templateUrl: './edit-place.component.html',
  styleUrls: [
    '../shared/styles/forms-style.css',
    './edit-place.component.css'
  ]
})
export class EditPlaceComponent implements OnInit, OnDestroy {

  place: ManagePlace;
  placeSubscription: Subscription;
  uploadImageSubscription: Subscription;
  deleteSubscription: Subscription;
  editPlaceForm: FormGroup;
  neighborhoodSearchForm: FormGroup;
  formSubmitted: boolean;
  numberOfSelectedAvailableHours = 0;
  selectedNeighborhood: boolean;
  title: string;
  subTitle: string;

  // step 1
  citiesSubscription: Subscription;
  cities: City[];
  neighborhoods: Neighborhood[];
  dynamicFormData: PublicsAreaSettingsModel;

  // step 2
  minimumParticipants = 1;
  maximumParticipants = 200;

  // step 3
  imageSource;

  // step 4
  formattedFromDate;
  dateRange = [];
  selectedDate;
  selectedHour;
  hours = this.manageDataService.hours;
  placeAvailability = {};

  faTrash = faTrash;

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private managePlaceService: ManagePlaceService,
    private festivalService: FestivalService,
    private manageDataService: ManageDataService
  ) {
  }

  ngOnInit(): void {
    const placeID = this.route.snapshot.paramMap.get('placeID');
    this.placeSubscription = this.managePlaceService.get(placeID).subscribe(data => {
      this.buildForm();

      this.place = data[0];
      this.title = this.place.description;
      this.subTitle = this.place.description;
      this.buildDates();
      this.setPlaceAvailableHours();

      this.http.get<PublicsAreaSettingsModel>(`${environment.apiURL}/data/add-initiative1`).subscribe(res => {
        this.dynamicFormData = res;
        this.setFormData();
      });

      this.citiesSubscription = this.manageDataService.getCities().subscribe(cities => {
        this.cities = cities;
        const cityValue = this.place.cityID ? this.place.cityID.toString() : 0;
        this.editPlaceForm.controls.cityID.setValue(cityValue);
        if (cityValue) {
          this.onSelectCity();
        }
      });

      // listen to uploaded file changes
      this.uploadImageSubscription = this.manageDataService.uploadResponse.subscribe(res => {
        if (res) {
          const uploadedFileData = res.body;
          this.setImageSource(uploadedFileData);
        }
      });
    });
  }

  buildForm(): void {
    this.editPlaceForm = new FormGroup({
      hostID: new FormControl('', Validators.required),
      placeID: new FormControl('', Validators.required),
      description: new FormControl('', Validators.required),
      cityID: new FormControl('', Validators.required),
      neighborhoodID: new FormControl(''),
      street: new FormControl('', Validators.required),
      houseNumber: new FormControl('', Validators.required),
      entrance: new FormControl(''),
      areaSettingValue: new FormControl('', Validators.required),
      hasParking: new FormControl(false),
      isAccessible: new FormControl(false),
      petFriendly: new FormControl(false),
      canHostNoisyEvents: new FormControl(false),
      piano: new FormControl(false),
      notes: new FormControl(''),
      numberOfInitiativesWillingToHost: new FormControl(''),
      availability: new FormControl(''),
      maxNumberOfPeople: new FormControl(''),
      fileHash: new FormControl('')
    });

    this.neighborhoodSearchForm = new FormGroup({
      search: new FormControl('')
    });
  }

  buildDates(): void {
    this.selectedDate = moment(this.place.festivalFromDate);
    for (const m = moment(this.place.festivalFromDate); m.diff(this.place.festivalUntilDate, 'days') <= 0; m.add(1, 'days')) {
      const fullDate = m.format('YYYY-MM-DD');
      this.setDataObject(fullDate);
      this.dateRange.push({
        day: m.locale('he').format('dddd'),
        dayMonth: m.format('DD/MM'),
        fullDate
      });
    }
    this.selectDate(this.place.festivalFromDate);
  }

  setPlaceAvailableHours(): void {
    this.place.availableDatetime.map(item => {
      const formattedDate = moment(item.date).format('YYYY-MM-DD');
      this.placeAvailability[formattedDate].hours = item.hours;
    });
  }

  setFormData(): void {
    for (const key of Object.keys(this.place)) {
      if (this.editPlaceForm.controls[key]) {
        this.editPlaceForm.controls[key].setValue(this.place[key]);
      }
    }

    if (this.place.path) {
      this.setImageSource(this.place);
    }
  }

  selectDate(date): void {
    this.selectedDate = date;
  }

  setDataObject(dateString): void {
    this.placeAvailability[dateString] = {hours: []};
  }

  selectHour(hour): void {
    if (!this.isHourDisabled(hour)) {
      if (this.placeAvailability[this.selectedDate]) {
        if (this.placeAvailability[this.selectedDate].hours.includes(hour)) {
          const indexOfExistingHour = this.placeAvailability[this.selectedDate].hours.findIndex(item => item === hour);
          if (indexOfExistingHour > -1) {
            this.placeAvailability[this.selectedDate].hours.splice(indexOfExistingHour, 1);
          }
        } else {
          this.placeAvailability[this.selectedDate].hours.push(hour);
        }
      }
      this.getNumberOfSelectedHours();
    }
  }

  getNumberOfSelectedHours(): void {
    let hours = 0;
    for (const date of Object.keys(this.placeAvailability)) {
      hours += this.placeAvailability[date].hours.length;
    }
    this.numberOfSelectedAvailableHours = hours;
  }

  isHourSelected(hour): boolean {
    return this.placeAvailability[this.selectedDate]?.hours.includes(hour) ?? false;
  }

  isHourUnavailable(hour): boolean {
    const notAvailableDate = this.place.placeIsNotAvailableAtDateTime.find(item => item.date === this.selectedDate);
    if (notAvailableDate) {
      return notAvailableDate?.fromHours.includes(hour) || notAvailableDate?.blockedHours.includes(hour) || false;
    }
    return false;
  }

  isEventStartingHour(hour): boolean {
    if (hour) {
      const dateSplitted = this.selectedDate.split(' ')[0];
      const notAvailableDate = this.place.placeIsNotAvailableAtDateTime.find(item => item.date === dateSplitted);
      if (notAvailableDate) {
        return notAvailableDate?.fromHours.includes(hour) || false;
      }
      return false;
    }
  }

  isHourDisabled(hour): boolean {
    const notAvailableDate = this.place.placeIsNotAvailableAtDateTime.find(item => item.date === this.selectedDate);
    if (notAvailableDate) {
      if (notAvailableDate.fromHours.includes(hour)) {
        return true;
      }
    }
    return false;
  }

  setImageSource(uploadedFile): void {
    this.imageSource = uploadedFile.path;
    this.editPlaceForm.controls.fileHash.setValue(uploadedFile.fileHash);
  }

  onSelectCity(): void {
    const cityID = this.editPlaceForm.value.cityID;
    if (cityID) {
      this.manageDataService.getNeighborhoods(cityID).subscribe(neighborhoods => {
        this.neighborhoods = neighborhoods;
        const neighborhoodID = this.place.neighborhoodID ? this.place.neighborhoodID.toString() : 0;
        const neighborhoodTitle = this.place.neighborhood ? this.place.neighborhood : 0;
        this.setNeighborhoodFormData(neighborhoodID, neighborhoodTitle);
      });
    }
  }

  setNeighborhoodFormData(neighborhoodID, neighborhoodTitle): void {
    if (neighborhoodID && neighborhoodTitle) {
      this.neighborhoodSearchForm.controls.search.setValue(neighborhoodTitle);
      this.editPlaceForm.controls.neighborhoodID.setValue(parseInt(neighborhoodID, 10));
      this.selectedNeighborhood = true;
    }
  }

  reformatAvailabilityDataToArray(): void {
    const availability = [];
    this.place.availableDatetime = [];
    for (const date of Object.keys(this.placeAvailability)) {
      const hours = this.placeAvailability[date].hours;
      availability.push(
        {
          date,
          hours
        }
      );
    }
    this.editPlaceForm.controls.availability.setValue(availability);
  }

  onSelectNeighborhood(neighborhood): void {
    this.setNeighborhoodFormData(neighborhood.neighborhoodID, neighborhood.title);
  }

  resetNeighborhoodSelect(): void {
    this.selectedNeighborhood = false;
    this.editPlaceForm.controls.neighborhoodID.setValue(null);
  }

  delete(): void {
    if (this.place.numberOfEventsAtPlace === 0) {
      const confirmDelete = confirm('האם את/ה בטוח שאת/ה רוצה למחוק את המקום שלך?');
      if (confirmDelete) {
        this.deleteSubscription = this.managePlaceService.delete(this.place.placeID).subscribe(() => {
          this.router.navigate([`/places-list`]);
        });
      }
    }
  }

  checkIfSubmitIsDisabled(): boolean {
    if (this.editPlaceForm.controls.numberOfInitiativesWillingToHost.value < this.place.events.length) {
      return true;
    }
    return false;
  }

  onSubmit(): void {
    this.formSubmitted = true;
    this.editPlaceForm.controls.placeID.setValue(this.place.placeID);
    this.editPlaceForm.controls.maxNumberOfPeople.setValue(this.place.maxNumberOfPeople);
    this.getNumberOfSelectedHours();

    if (this.editPlaceForm.status === 'VALID' && this.numberOfSelectedAvailableHours > 0 &&
      this.numberOfSelectedAvailableHours >= this.editPlaceForm.controls.numberOfInitiativesWillingToHost.value) {
      this.reformatAvailabilityDataToArray();
      this.managePlaceService.update(this.editPlaceForm.value).subscribe(res => {
        this.router.navigate([`/places-list`]);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.citiesSubscription) {
      this.citiesSubscription.unsubscribe();
    }
    if (this.uploadImageSubscription) {
      this.uploadImageSubscription.unsubscribe();
    }
    if (this.deleteSubscription) {
      this.deleteSubscription.unsubscribe();
    }
  }

}
